import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

const saleLabel = s => (s && s.saleNumber > 0 ? `#${s.saleNumber}` : s && s.id ? `id ${s.id}` : '–');

@observer
class SaleMergeDialog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            candidates: [],
            selected: null,
            loading: true,
            submitting: false,
            error: '',
        };
    }

    async componentDidMount() {
        const { primarySale, stores } = this.props;
        const { saleStore } = stores;
        // The top-level `sale.visitor` field is often 0 — the reliable visitor id lives in visitors[0].id.
        const visitorId = (primarySale.visitors && primarySale.visitors[0] && primarySale.visitors[0].id)
            || primarySale.visitor
            || 0;
        if (!visitorId) {
            this.setState({ loading: false, error: 'This sale has no visitor — cannot find candidates.' });
            return;
        }
        try {
            const response = await saleStore.load({
                query: { visitor: visitorId, limit: 50 },
                skipUpdate: true,
            });
            const all = Array.isArray(response) ? response : [];
            const candidates = all.filter((s) => {
                if (!s || s.id === primarySale.id) return false;
                if (s.offline === 1) return false;
                if (s.mergedInto && s.mergedInto > 0) return false;
                const hasProducts = (Array.isArray(s.products) && s.products.length > 0)
                    || (Array.isArray(s.productsImported) && s.productsImported.length > 0);
                if (hasProducts) return false;
                // Only show sales that have something worth merging (animal or journal)
                const hasAnimal = Array.isArray(s.animals) && s.animals.length > 0;
                const hasJournal = Array.isArray(s.journals) && s.journals.length > 0;
                return hasAnimal || hasJournal;
            });
            this.setState({ candidates, loading: false });
        } catch (err) {
            this.setState({ loading: false, error: (err && err.message) || 'Failed to load candidates.' });
        }
    }

    closeDrawer = () => {
        const { stores, drawerLevel } = this.props;
        const { appState } = stores;
        appState.toggleDrawer(false, drawerLevel);
    }

    handleSelect = (candidate) => {
        this.setState({ selected: candidate, error: '' });
    }

    handleBack = () => {
        this.setState({ selected: null, error: '' });
    }

    handleConfirm = async () => {
        const { primarySale, stores, onMerged } = this.props;
        const { saleStore } = stores;
        const { selected } = this.state;
        if (!selected) return;
        this.setState({ submitting: true, error: '' });
        try {
            const response = await saleStore.mergeSale({
                primaryId: primarySale.id,
                secondaryId: selected.id,
            });
            const ok = response && response.status >= 200 && response.status < 300;
            if (ok) {
                this.setState({ submitting: false });
                if (onMerged) onMerged();
                this.closeDrawer();
            } else {
                this.setState({
                    submitting: false,
                    error: (response && response.message) || 'Merge failed.',
                });
            }
        } catch (err) {
            this.setState({ submitting: false, error: (err && err.message) || 'Merge failed.' });
        }
    }

    render() {
        const { primarySale } = this.props;
        const { candidates, selected, loading, submitting, error } = this.state;

        const selectedAnimal = selected && Array.isArray(selected.animals) && selected.animals[0];
        const selectedJournal = selected && Array.isArray(selected.journals) && selected.journals[0];
        const willAppendDescription = !!(selected && selected.description
            && (!primarySale.description || primarySale.description === ''));

        return (
            <div class='container-fluid pt-5 pb-5'>
                <div class='row'>
                    <div class='col-12'>
                        <h4 class='mb-3'>
                            <i class='fa-duotone fa-merge me-2' />
                            <Text id='sale.merge.dialogTitle' fields={{ saleNumber: saleLabel(primarySale) }}>
                                Merge another sale into {saleLabel(primarySale)}
                            </Text>
                        </h4>
                        <p class='text-body-secondary'>
                            <Text id='sale.merge.subtitle'>Pick another sale for the same visitor that has an animal or journal but no products.</Text>
                        </p>

                        {error && (
                            <div class='alert alert-danger'>
                                <i class='fa-duotone fa-triangle-exclamation me-2' />
                                {error}
                            </div>
                        )}

                        {loading && (
                            <div class='d-flex align-items-center text-body-secondary py-4'>
                                <div class='spinner-border spinner-border-sm me-2' role='status'>
                                    <span class='visually-hidden'>Loading...</span>
                                </div>
                                Loading…
                            </div>
                        )}

                        {!loading && !selected && candidates.length === 0 && (
                            <div class='text-body-secondary py-3'>
                                <Text id='sale.merge.noCandidates'>No mergeable sales found for this visitor.</Text>
                            </div>
                        )}

                        {!loading && !selected && candidates.length > 0 && (
                            <div class='list-group'>
                                {candidates.map(c => (
                                    <button
                                        key={c.id}
                                        type='button'
                                        class='list-group-item list-group-item-action'
                                        onClick={() => this.handleSelect(c)}
                                    >
                                        <div class='d-flex justify-content-between'>
                                            <strong>{saleLabel(c)}</strong>
                                            <small class='text-body-secondary'>
                                                {util.formatDate(c.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                                            </small>
                                        </div>
                                        <small class='text-body-secondary d-block'>
                                            {(c.animals && c.animals[0] && c.animals[0].name) || '—'}
                                            {' · '}
                                            {(c.journals && c.journals.length) || 0} journal(s)
                                        </small>
                                    </button>
                                ))}
                            </div>
                        )}

                        {selected && (
                            <div>
                                <h6><Text id='sale.merge.confirmHeading'>Confirm merge</Text></h6>
                                <ul>
                                    {selectedAnimal && (
                                        <li>
                                            <Text id='sale.merge.willCopyAnimal' fields={{ name: selectedAnimal.name || `#${selectedAnimal.id}` }}>
                                                Animal will be added to this sale
                                            </Text>
                                        </li>
                                    )}
                                    {selectedJournal && (
                                        <li>
                                            <Text id='sale.merge.willCopyJournal' fields={{ id: selectedJournal.id }}>
                                                Journal will be added to this sale
                                            </Text>
                                        </li>
                                    )}
                                    {willAppendDescription && (
                                        <li>
                                            <Text id='sale.merge.willAppendDescription'>
                                                Note from secondary will be appended to this sale's description
                                            </Text>
                                        </li>
                                    )}
                                    <li>
                                        <Text id='sale.merge.willMarkSecondary' fields={{ saleNumber: saleLabel(selected) }}>
                                            Sale {saleLabel(selected)} will be marked as merged and hidden
                                        </Text>
                                    </li>
                                </ul>
                                <div class='d-flex justify-content-between mt-4'>
                                    <button type='button' class='btn btn-outline-secondary' onClick={this.handleBack} disabled={submitting}>
                                        <i class='fa-duotone fa-chevron-left me-1' />
                                        Back
                                    </button>
                                    <div>
                                        <button type='button' class='btn btn-secondary me-2' onClick={this.closeDrawer} disabled={submitting}>
                                            <Text id='sale.merge.cancel'>Cancel</Text>
                                        </button>
                                        <button type='button' class='btn btn-danger' onClick={this.handleConfirm} disabled={submitting}>
                                            {submitting
                                                ? <span><span class='spinner-border spinner-border-sm me-2' role='status' />…</span>
                                                : <Text id='sale.merge.confirm'>Confirm merge</Text>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SaleMergeDialog;
